/**
 * Der Sitzungs-Token im Browser.
 *
 * §46 verlangt, dass ein Spieler nach einem Verbindungsabbruch **als derselbe Spieler**
 * zurückkommt — mit seiner Farbe, seiner Aufstellung und seiner offenen Preview (§39). Dafür
 * muss der Token den Tab überleben, also `localStorage` und nicht Speicher im Prozess.
 *
 * **Er steht in keiner Adresse.** Ein Host- oder Einladungslink soll geteilt werden, ein
 * Sitzungslink nicht: wer ihn bekäme, *wäre* dieser Spieler. Deshalb trägt `routing/fragment.ts`
 * ihn nicht.
 *
 * **Jeder Zugriff ist umschlossen.** In einem privaten Fenster oder bei blockierten
 * Website-Daten wirft schon das Lesen. Das ist ein Verlust an Bequemlichkeit — der Spieler muss
 * seinen Einladungslink erneut öffnen — und darf kein Absturz sein.
 *
 * **Es gibt genau eine gespeicherte Sitzung.** Ein Token gehört zu **einer** Partie (der Server
 * leitet sie daraus ab), und wer einem zweiten Einladungslink folgt, ersetzt damit den ersten:
 * für die alte Partie braucht er ihren Link noch einmal. Bewusst so — eine Verwaltung mehrerer
 * Sitzungen bräuchte eine Ansicht, aus der man wählt, und die gibt es nicht. Ein Testspieler in
 * zwei gleichzeitigen Partien ist der Fall, in dem das auffallen würde; dann ist es eine
 * Erweiterung mit eigener Entscheidung und kein Nachtrag.
 */

/** Der Schlüssel im `localStorage`. Mit Namensraum, damit er neben anderem nicht kollidiert. */
export const SESSION_KEY = 'flaghex.session';

/**
 * Der gespeicherte Token, oder `undefined`.
 *
 * Leerraum wird abgeschnitten: ein von Hand eingefügter Token bringt leicht ein Leerzeichen
 * mit, und als Header wäre er damit ungültig — die Meldung hieße dann „nicht berechtigt" statt
 * „Link unvollständig". Bleibt nichts übrig, ist es **keine** Sitzung; dieselbe Unterscheidung
 * wie serverseitig in `tokens.ts`.
 */
export function readSession(): string | undefined {
  let stored: string | null;
  try {
    stored = window.localStorage.getItem(SESSION_KEY);
  } catch {
    return undefined;
  }
  const token = stored?.trim() ?? '';
  return token === '' ? undefined : token;
}

/**
 * Speichert den Token.
 *
 * Ein leerer Token ist ein **Fehler im Programm** und wird laut abgewiesen: still gespeichert
 * ergäbe er einen angemeldeten Zustand ohne Berechtigung, und der Fehler fiele erst beim
 * nächsten Verbindungsversuch auf — weit entfernt von seiner Ursache.
 *
 * Ein blockierter Speicher ist dagegen **kein** Fehler des Programms und wird geschluckt. Der
 * Spieler spielt dann ohne Wiedererkennung über ein Neuladen hinweg.
 */
export function writeSession(token: string): void {
  const trimmed = token.trim();
  if (trimmed === '') {
    throw new Error('Ein leerer Sitzungs-Token ist keine Sitzung');
  }
  try {
    window.localStorage.setItem(SESSION_KEY, trimmed);
  } catch {
    // Absichtlich still: siehe Modulkopf.
  }
}

/** Vergisst die Sitzung — nach einem Partieende oder einer abgewiesenen Anmeldung. */
export function clearSession(): void {
  try {
    window.localStorage.removeItem(SESSION_KEY);
  } catch {
    // Absichtlich still: siehe Modulkopf.
  }
}
